import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useAppSettings } from "@/hooks/use-app-settings";
import { Application } from "@shared/schema";

type EditApplicationDialogProps = {
  applicationId: number | null;
  isOpen: boolean;
  onClose: () => void;
};

// Define the form schema using zod
const formSchema = z.object({
  name: z.string().min(1, "Application name is required"),
  category: z.string().min(1, "Category is required"),
  description: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

export default function EditApplicationDialog({
  applicationId,
  isOpen,
  onClose,
}: EditApplicationDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { getSettingsByType } = useAppSettings();

  const categoryOptions = getSettingsByType("category")
    .filter((setting) => setting.isActive)
    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
    .map((setting) => setting.value);

  // Query the application data when the dialog is open and an ID is provided
  const { data: application, isLoading } = useQuery<Application>({
    queryKey: ['/api/applications', applicationId],
    queryFn: async () => {
      if (!applicationId) throw new Error("Application ID is required");
      const res = await fetch(`/api/applications/${applicationId}`);
      if (!res.ok) {
        throw new Error('Failed to fetch application');
      }
      return res.json();
    },
    enabled: isOpen && applicationId !== null,
  });

  // Create form
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      category: "",
      description: "",
    },
  });

  const selectedCategory = form.watch("category") || application?.category || "";
  const allCategoryOptions = Array.from(
    new Set([
      ...categoryOptions,
      selectedCategory,
      "Other",
    ].filter(Boolean))
  );

  // Update form values when application data is loaded
  useEffect(() => {
    if (application) {
      // Log the application data for debugging
      console.log('Application data loaded:', application);
      
      form.reset({
        name: application.name,
        category: application.category || "Other",
        description: application.description || "",
      });
    }
  }, [application, form]);

  // Mutation for updating an application
  const updateMutation = useMutation({
    mutationFn: async (values: FormValues) => {
      if (!applicationId) throw new Error("Application ID is required");
      
      console.log('🚀 Making PUT request to:', `/api/applications/${applicationId}`);
      console.log('🚀 Request body:', values);
      
      const result = await apiRequest("PUT", `/api/applications/${applicationId}`, values);
      console.log('✅ Update successful:', result);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/applications'] });
      toast({
        title: "Application updated",
        description: "The application has been updated successfully.",
      });
      onClose();
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to update application",
        description: error.message,
        variant: "destructive",
      });
    },
    onSettled: () => {
      setIsSubmitting(false);
    }
  });

  // Submit handler
  const onSubmit = (values: FormValues) => {
    console.log('🔄 Form submission started with values:', values);
    console.log('🔄 Application ID:', applicationId);
    console.log('🔄 Form validation errors:', form.formState.errors);
    
    
    console.log('🔄 Final values being sent:', values);
    setIsSubmitting(true);
    updateMutation.mutate(values);
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Application</DialogTitle>
          <DialogDescription>
            Update the application information
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-4 mt-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 mt-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Application Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter application name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />


              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a category" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="z-[70] max-h-64">
                        {allCategoryOptions.map((category) => (
                          <SelectItem key={category} value={category}>
                            {category}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />


              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description (optional)</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Enter application description"
                        rows={3}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />


              <DialogFooter>
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Saving..." : "Save Changes"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
